// src/pages/CreateEvent.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const MAX_IMG_MB = 8;

function pad2(n) { return String(n).padStart(2, "0"); }
function addDays(dateStr, days) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, (d || 1) + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}
function joinLocal(dateStr, timeStr) {
  // "YYYY-MM-DD" + "HH:mm" -> "YYYY-MM-DDTHH:mm:00"
  if (!dateStr || !timeStr) return "";
  return `${dateStr}T${timeStr}:00`;
}

export default function CreateEvent() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "",
    date: "",
    startTime: "",
    endTime: "",
    totalStandardTickets: "",
    standardTicketPrice: "",
    totalVipTickets: "",
    vipTicketPrice: "",
    planned: false, // Coming soon toggle
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const blobRef = useRef("");

  useEffect(() => {
    return () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    };
  }, []);

  const onChange = (e) => {
    const { name, value, type } = e.target;
    const isNum = type === "number" || name.includes("Tickets") || name.includes("Price");
    setForm((f) => ({
      ...f,
      [name]: isNum
        ? (value === "" ? "" : Number(value))  // keep "" when cleared
        : value
    }));
  };

  const onImage = (e) => {
    setError("");
    const f = e.target.files?.[0];
    if (!f) {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      blobRef.current = "";
      setImageFile(null);
      setPreviewUrl("");
      return;
    }
    const sizeMb = f.size / (1024 * 1024);
    if (sizeMb > MAX_IMG_MB) {
      setError(`Image is too large (max ${MAX_IMG_MB}MB).`);
      return;
    }
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    const url = URL.createObjectURL(f);
    blobRef.current = url;
    setImageFile(f);
    setPreviewUrl(url);
  };

  const clearImage = () => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    blobRef.current = "";
    setImageFile(null);
    setPreviewUrl("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // prevent double submit
    setMessage("");
    setError("");
    setLoading(true);

    try {
      await api.get("/api/auth/me").catch(() => { }); // ignore errors here
      const {
        title, description, location, date,
        startTime, endTime,
        totalStandardTickets, standardTicketPrice,
        totalVipTickets, vipTicketPrice,
      } = form;

      const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));


      // Only enforce date/time when NOT planned
      if (!form.planned && (!date || !startTime || !endTime)) {
        setLoading(false);
        return setError("Please select date, start time, and end time.");
      }

      // Build LocalDateTime strings if values exist.
      // If endTime is earlier than startTime, roll end date to the next day.
      const endDateStr =
        (date && endTime && startTime && endTime < startTime)
          ? addDays(date, 1)
          : date;

      const startDateTime = (date && startTime) ? joinLocal(date, startTime) : null;
      const endDateTime = (endDateStr && endTime) ? joinLocal(endDateStr, endTime) : null;

      // IMPORTANT CHANGE:
      // Do NOT null-out fields just because it's planned.
      // Keep what the user typed so "TBC" stays as-is unless the DB column is actually null.
      const data = {
        title: title.trim(),
        description,
        location: (location && location.trim()) ? location.trim() : null,
        date: date || null,
        startTime: startDateTime,
        endTime: endDateTime,
        totalStandardTickets: numOrNull(totalStandardTickets),
        standardTicketPrice: numOrNull(standardTicketPrice),
        totalVipTickets: numOrNull(totalVipTickets),
        vipTicketPrice: numOrNull(vipTicketPrice),
        status: form.planned ? "PLANNED" : "PUBLISHED",
      };

      const fd = new FormData();
      fd.append("data", new Blob([JSON.stringify(data)], { type: "application/json" }));
      if (imageFile) fd.append("image", imageFile);

      await api.post("/api/admin/events/create", fd);
      // 2) Redirect like EditEvent
      navigate("/admin/events", { replace: true });
    } catch (err) {
      // If the server created the record but responds 403 (what you're seeing),
      // behave like success so the UX matches EditEvent.
      if (err?.response?.status === 403) {
        return navigate("/admin/events", { replace: true });
      }
      const raw = err?.response?.data;
      const msg =
        (typeof raw === "string" && raw) ||
        raw?.message ||
        err?.message ||
        "Failed to create event.";
      setError(msg);
      // make sure the error is visible
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page space-y-6">
      {/* Header */}
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Create Event</h1>
            <p className="mt-1 text-slate-300">
              Add details, set ticketing, upload artwork, and publish.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              form="create-event-form"
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? "Creating…" : "Create Event"}
            </button>
          </div>
        </div>

        {(message || error) && (
          <div className="mt-3">
            {message && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200">
                {message}
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
                {error}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Form */}
      <form id="create-event-form" onSubmit={submit} className="grid gap-4 md:grid-cols-3">
        {/* LEFT: Details */}
        <section className="card md:col-span-2">
          <h2 className="text-xl font-semibold text-white">Event Details</h2>
          <div className="mt-4 grid gap-3">
            <Field label="Title *">
              <input
                name="title"
                value={form.title}
                onChange={onChange}
                className="input w-full"
                placeholder="Event title"
                required
              />
            </Field>

            <Field label="Description">
              <textarea
                name="description"
                value={form.description}
                onChange={onChange}
                className="input w-full min-h-[120px]"
                placeholder="Tell people about this event"
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Location *">
                <input
                  name="location"
                  value={form.location}
                  onChange={onChange}
                  className="input w-full"
                  placeholder="Venue or address"
                  required={!form.planned}
                />
              </Field>

              <Field label="Date *">
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={onChange}
                  className="input w-full"
                  required={!form.planned}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Start Time *">
                <input
                  type="time"
                  name="startTime"
                  value={form.startTime}
                  onChange={onChange}
                  className="input w-full"
                  required={!form.planned}
                />
              </Field>
              <Field label="End Time *">
                <input
                  type="time"
                  name="endTime"
                  value={form.endTime}
                  onChange={onChange}
                  className="input w-full"
                  required={!form.planned}
                />
              </Field>
            </div>
            <p className="text-xs text-slate-400">
              If the end time is earlier than the start time, we’ll treat it as an
              overnight event (ends the next day).
            </p>
          </div>
        </section>

        {/* RIGHT: Artwork */}
        <section className="card">
          <h2 className="text-xl font-semibold text-white">Artwork</h2>
          <div className="mt-4 grid gap-3">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <div className="aspect-[4/3] w-full">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Event artwork preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center text-sm text-slate-400">
                    No image selected
                  </div>
                )}
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/10" />
            </div>

            <label className="text-[11px] uppercase tracking-wider text-slate-400">
              Upload image (max {MAX_IMG_MB}MB)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={onImage}
              className="input file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-slate-100 hover:file:bg-white/15"
            />
            {imageFile && (
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span className="truncate">
                  {imageFile.name} • {Math.round(imageFile.size / 1024)} KB
                </span>
                <button type="button" className="btn-secondary !px-2 !py-1" onClick={clearImage}>
                  Remove
                </button>
              </div>
            )}
          </div>
        </section>

        {/* BOTTOM: Tickets & Pricing */}
        <section className="card md:col-span-3">
          <h2 className="text-xl font-semibold text-white">Tickets & Pricing</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="font-medium text-white">Standard</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Total">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    name="totalStandardTickets"
                    value={form.totalStandardTickets}
                    onChange={onChange}
                    className="input w-full"
                  />
                </Field>
                <Field label="Price (£)">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    name="standardTicketPrice"
                    value={form.standardTicketPrice}
                    onChange={onChange}
                    className="input w-full"
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <h3 className="font-medium text-white">VIP</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Total">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    name="totalVipTickets"
                    value={form.totalVipTickets}
                    onChange={onChange}
                    className="input w-full"
                  />
                </Field>
                <Field label="Price (£)">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    name="vipTicketPrice"
                    value={form.vipTicketPrice}
                    onChange={onChange}
                    className="input w-full"
                  />
                </Field>
              </div>
            </div>
          </div>
        </section>

        <section className="card md:col-span-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.planned}
              onChange={(e) => setForm(f => ({ ...f, planned: e.target.checked }))}
            />
            <span className="text-slate-200">This is a Coming Soon (TBC) event</span>
          </label>
          <p className="mt-1 text-sm text-slate-400">
            Users can see the listing but can’t purchase until you publish full details.
          </p>
        </section>

        {/* Submit bar for small screens */}
        <div className="md:hidden">
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? "Creating…" : "Create Event"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Little form helper ---------- */
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}
